import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Button, 
  Input, 
  Modal,
  Form,
  Select,
  message,
  Space,
  Tag,
  Tree,
  Popconfirm,
  Dropdown
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SearchOutlined,
  FolderOutlined,
  FileOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { specService } from '../services/specService';
import type { SpecItem } from '../types/spec';
import { useLanguage } from '../contexts/LanguageContext';

// 扩展类型定义
interface SpecItemVO {
  id: number;
  pId: number;
  sdkProductId: number;
  category: string;
  name: string;
  nameEn?: string;
  namePl?: string;
  isExist: number;
  gmtCreate: string;
  gmtModified: string;
  remark?: string;
  children?: SpecItemVO[];
  level?: number;
  isLeaf?: boolean;
}

const { Option } = Select;
const { Search } = Input;

// 语言类型定义
type LanguageType = 'zh' | 'en' | 'pl';

// 语言配置
const LANGUAGE_CONFIG = {
  zh: { flag: '🇨🇳', name: '中文名称', key: 'name' },
  en: { flag: '🇺🇸', name: '英文名称', key: 'nameEn' },
  pl: { flag: '🇵🇱', name: '波兰语名称', key: 'namePl' }
};

interface TreeNode {
  title: React.ReactNode;
  key: string;
  children?: TreeNode[];
  specItem?: SpecItemVO;
}

const SpecManagement: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<SpecItemVO | null>(null);
  const [parentId, setParentId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<SpecItemVO | null>(null);
  const [rightPanelMode, setRightPanelMode] = useState<'detail' | 'add' | 'edit'>('detail');
  const [parentLanguage, setParentLanguage] = useState<LanguageType>('zh');
  const [highlightedNodes, setHighlightedNodes] = useState<Set<number>>(new Set());
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [originalSpecData, setOriginalSpecData] = useState<SpecItemVO[]>([]);
  const highlightTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchSpecTree();
  }, []);

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, []);


  // 获取规格树形结构
  const fetchSpecTree = async () => {
    setLoading(true);
    try {
      const response = await specService.getSpecTree();
      console.log('API响应:', response); // 调试用
      
      // 检查响应结构
      const specs = response?.data || response || [];
      console.log('处理后的数据:', specs); // 调试用
      
      // 保存原始数据
      setOriginalSpecData(specs);
      
      // 初始加载时不传递选中状态，让组件自己管理
      const treeNodes = convertToTreeData(specs, [], null);
      setTreeData(treeNodes);
    } catch (error) {
      console.error('获取规格列表失败:', error);
      message.error('获取规格列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 模糊匹配节点
  const findMatchingNodes = (specs: SpecItemVO[], searchKey: string): number[] => {
    const matchedIds: number[] = [];
    
    const searchInNode = (node: SpecItemVO) => {
      // 检查中文、英文、波兰名称是否匹配
      const nameMatch = node.name?.toLowerCase().includes(searchKey.toLowerCase());
      const nameEnMatch = node.nameEn?.toLowerCase().includes(searchKey.toLowerCase());
      const namePlMatch = node.namePl?.toLowerCase().includes(searchKey.toLowerCase());
      
      if (nameMatch || nameEnMatch || namePlMatch) {
        matchedIds.push(node.id);
      }
      
      // 递归搜索子节点
      if (node.children) {
        node.children.forEach(child => searchInNode(child));
      }
    };
    
    specs.forEach(spec => searchInNode(spec));
    return matchedIds;
  };

  // 收集需要展开的节点路径
  const collectExpandedKeys = (specs: SpecItemVO[], matchedIds: number[]): React.Key[] => {
    const expandedKeys: React.Key[] = [];
    
    const findPath = (node: SpecItemVO, targetId: number, path: number[] = []): number[] | null => {
      const currentPath = [...path, node.id];
      
      if (node.id === targetId) {
        return currentPath;
      }
      
      if (node.children) {
        for (const child of node.children) {
          const result = findPath(child, targetId, currentPath);
          if (result) return result;
        }
      }
      
      return null;
    };
    
    matchedIds.forEach(matchedId => {
      for (const spec of specs) {
        const path = findPath(spec, matchedId);
        if (path) {
          // 将路径上的所有节点ID添加到展开列表中
          path.forEach(id => {
            if (!expandedKeys.includes(id.toString())) {
              expandedKeys.push(id.toString());
            }
          });
          break;
        }
      }
    });
    
    return expandedKeys;
  };

  // 递归转换树形数据
  const convertToTreeData = (specs: SpecItemVO[], matchedIds: number[] = [], currentSelectedItem: SpecItemVO | null = null): TreeNode[] => {
    console.log('原始数据:', specs); // 调试用
    console.log('当前选中项:', currentSelectedItem); // 调试用

    // 如果后端已经构建好了树形结构，直接转换
    return specs.map(spec => convertSpecToTreeNode(spec, matchedIds, currentSelectedItem));
  };

  // 转换单个规格为树节点
  const convertSpecToTreeNode = (spec: SpecItemVO, matchedIds: number[] = [], currentSelectedItem: SpecItemVO | null = null): TreeNode => {
    const isHighlighted = matchedIds.includes(spec.id);
    const isSelected = currentSelectedItem?.id === spec.id;
    
    // 调试信息
    if (isHighlighted) {
      console.log(`节点 ${spec.id} (${spec.name}) 被高亮`);
    }
    if (isSelected) {
      console.log(`节点 ${spec.id} (${spec.name}) 被选中`);
    }
    
    const node: TreeNode = {
        title: (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              padding: '8px 0',
              cursor: 'pointer',
              borderRadius: '4px',
              paddingLeft: '8px',
              paddingRight: '8px',
              backgroundColor: isSelected ? '#e6f7ff' : (isHighlighted ? '#fff2e8' : 'transparent'),
              border: isSelected ? '1px solid #1890ff' : (isHighlighted ? '1px solid #fa8c16' : '1px solid transparent'),
              transition: 'all 0.3s ease'
            }}
            onClick={() => {
              // 点击时清除高亮状态
              setHighlightedNodes(new Set());
              if (highlightTimerRef.current) {
                clearTimeout(highlightTimerRef.current);
                highlightTimerRef.current = null;
              }
              
              // 调用详情接口获取最新数据
              handleSelectItem(spec);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              {spec.category === 'option' ? (
                <FolderOutlined style={{ color: '#1890ff', marginRight: 8 }} />
              ) : (
                <FileOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <span style={{ fontWeight: 500, minWidth: '50px', color: '#666' }}>{spec.id}</span>
                <span style={{ minWidth: '180px', fontSize: '14px' }}>{spec.name}</span>
                <span style={{ color: '#666', fontSize: '13px', minWidth: '160px' }}>
                  {spec.nameEn || '-'}
                </span>
                <span style={{ color: '#999', fontSize: '13px', minWidth: '160px' }}>
                  {spec.namePl || '-'}
                </span>
                <Tag color={spec.category === 'option' ? 'blue' : 'green'}>
                  {spec.category === 'option' ? '分类' : '内容'}
                </Tag>
              </div>
            </div>
          <Space>
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleAddChild(spec.id);
              }}
              disabled={spec.category === 'content'}
              title={spec.category === 'content' ? '内容下不能新增子级' : '新增子级'}
            >
              新增子级
            </Button>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(spec);
              }}
            >
              编辑
            </Button>
            <Popconfirm
              title={t('confirmDeleteTitle')}
              description={t('confirmDeleteSpec')}
              onConfirm={(e) => {
                e?.stopPropagation();
                handleDelete(spec.id);
              }}
              okText={t('confirm')}
              cancelText={t('cancel')}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        </div>
      ),
      key: spec.id.toString(),
      specItem: spec,
      children: spec.children && spec.children.length > 0 
        ? spec.children.map(child => convertSpecToTreeNode(child, matchedIds, currentSelectedItem))
        : undefined
    };

    return node;
  };

  // 搜索
  const handleSearch = async (value: string) => {
    // 清除之前的定时器
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    
    setSearchText(value);
    setLoading(true);
    try {
      // 获取完整的规格树数据
      const response = await specService.getSpecTree();
      const results = response?.data || response || [];
      
      // 保存原始数据
      setOriginalSpecData(results);
      
      if (value && value.trim()) {
        // 前端模糊匹配
        const matchedNodes = findMatchingNodes(results, value.trim());
        const treeNodes = convertToTreeData(results, matchedNodes, null); // 搜索时清除选中状态
        setTreeData(treeNodes);
        
        // 收集需要展开的节点路径
        const keysToExpand = collectExpandedKeys(results, matchedNodes);
        setExpandedKeys(keysToExpand);
        
        // 搜索后清除选中状态，避免与高亮混淆
        setSelectedItem(null);
        setRightPanelMode('detail');
        
        // 高亮匹配的节点
        if (matchedNodes.length > 0) {
          console.log('设置高亮节点:', matchedNodes);
          setHighlightedNodes(new Set(matchedNodes));
          
          // 使用 ref 保存定时器引用，3秒后清除高亮
          highlightTimerRef.current = setTimeout(() => {
            console.log('3秒后清除高亮');
            setHighlightedNodes(new Set());
            highlightTimerRef.current = null;
          }, 3000);
        }
      } else {
        // 没有搜索关键词，显示完整树
        const treeNodes = convertToTreeData(results, [], null); // 清空搜索时也清除选中状态
        setTreeData(treeNodes);
        setExpandedKeys([]);
        setHighlightedNodes(new Set());
        // 清空搜索时也清除选中状态
        setSelectedItem(null);
        setRightPanelMode('detail');
      }
    } catch (error: any) {
      console.error('搜索失败:', error);
      const errorMessage = error.message || '搜索失败';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  // 选择规格项
  const handleSelectItem = async (item: SpecItemVO) => {
    try {
      // 调用详情接口获取最新数据
      const response = await specService.getSpecDetail(item.id);
      const latestItem = response.data;
      setSelectedItem(latestItem);
      setRightPanelMode('detail');
      
      // 重新构建树形数据以反映最新的选中状态
      if (originalSpecData.length > 0) {
        const treeNodes = convertToTreeData(originalSpecData, [], latestItem);
        setTreeData(treeNodes);
      }
    } catch (error) {
      console.error('获取规格详情失败:', error);
      // 如果接口失败，仍然显示原有数据
      setSelectedItem(item);
      setRightPanelMode('detail');
      
      // 重新构建树形数据以反映最新的选中状态
      if (originalSpecData.length > 0) {
        const treeNodes = convertToTreeData(originalSpecData, [], item);
        setTreeData(treeNodes);
      }
    }
  };

  // 获取父级名称（支持多语言）
  const getParentName = (parentId: number, language: LanguageType = 'zh'): string => {
    const findInTree = (nodes: TreeNode[]): SpecItemVO | null => {
      for (const node of nodes) {
        if (node.specItem?.id === parentId) {
          return node.specItem;
        }
        if (node.children) {
          const found = findInTree(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    const parentItem = findInTree(treeData);
    if (!parentItem) return `ID: ${parentId}`;
    
    const languageKey = LANGUAGE_CONFIG[language].key;
    const displayName = parentItem[languageKey as keyof SpecItemVO] as string;
    
    if (!displayName || displayName.trim() === '') {
      // 如果当前语言没有名称，回退到中文
      return parentItem.name || `ID: ${parentId}`;
    }
    
    return displayName;
  };

  // 新增根级规格
  const handleAdd = () => {
    setEditingItem(null);
    setParentId(0);
    setSelectedItem(null);
    setRightPanelMode('add');
    form.resetFields();
    form.setFieldsValue({ category: 'option' });
  };

  // 新增子级规格
  const handleAddChild = (parentId: number) => {
    setEditingItem(null);
    setParentId(parentId);
    setSelectedItem(null);
    setRightPanelMode('add');
    form.resetFields();
    form.setFieldsValue({ category: 'content', pId: parentId });
  };

  // 编辑
  const handleEdit = async (item: SpecItemVO) => {
    try {
      // 调用详情接口获取最新数据
      const response = await specService.getSpecDetail(item.id);
      const latestItem = response.data;
      
      setEditingItem(latestItem);
      setParentId(latestItem.pId);
      setSelectedItem(latestItem);
      setRightPanelMode('edit');
      form.setFieldsValue({
        ...latestItem,
        category: latestItem.category
      });
    } catch (error) {
      console.error('获取规格详情失败:', error);
      // 如果接口失败，仍然使用原有数据
      setEditingItem(item);
      setParentId(item.pId);
      setSelectedItem(item);
      setRightPanelMode('edit');
      form.setFieldsValue({
        ...item,
        category: item.category
      });
    }
  };

  // 删除
  const handleDelete = async (id: number) => {
    try {
      await specService.deleteSpec(id);
      message.success('删除成功');
      fetchSpecTree();
    } catch (error: any) {
      const errorMessage = error.message || '删除失败';
      message.error(errorMessage);
    }
  };

  // 保存
  const handleSave = async (values: any) => {
    try {
      if (editingItem) {
        await specService.updateSpec(editingItem.id, values);
        
        // 更新成功后，重新获取详情数据来更新选中状态
        try {
          const response = await specService.getSpecDetail(editingItem.id);
          const updatedItem = response.data;
          setSelectedItem(updatedItem);
        } catch (detailError) {
          console.error('获取更新后的详情失败:', detailError);
          // 如果获取详情失败，清空选中状态
          setSelectedItem(null);
        }
        
        message.success('更新成功');
      } else {
        // 根据parentId判断是顶级规格还是子级规格
        if (parentId === 0 || parentId === null) {
          // 新增顶级规格（pId固定为0）
          await specService.addTopLevelSpec(values);
          message.success('新增顶级规格成功');
        } else {
          // 新增子级规格
          await specService.addSubSpec(parentId, values);
          message.success('新增子级规格成功');
        }
        
        // 新增成功后清空选中状态
        setSelectedItem(null);
      }
      setRightPanelMode('detail');
      fetchSpecTree();
    } catch (error: any) {
      const errorMessage = error.message || (editingItem ? '更新失败' : '新增失败');
      message.error(errorMessage);
    }
  };

  // 使用服务端搜索结果，不需要客户端过滤
  const filteredTreeData = treeData;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('specManagement')}</h1>
      </div>

      <div style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 120px)' }}>
        {/* 左侧列表区域 */}
        <div style={{ flex: '0 0 60%' }}>
          <Card className="content-card" style={{ height: '100%' }}>
            <div className="page-actions">
              <Search
                placeholder={t('searchSpecName')}
                allowClear
                enterButton={<SearchOutlined />}
                size="middle"
                onSearch={handleSearch}
                style={{ width: 300 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增规格
              </Button>
            </div>

            <div style={{ marginTop: 16, height: 'calc(100% - 80px)', overflow: 'auto' }}>
              {/* 表头 */}
              <div style={{ 
                background: '#fafafa', 
                padding: '12px 16px', 
                border: '1px solid #f0f0f0',
                borderBottom: 'none',
                borderRadius: '6px 6px 0 0',
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#666'
              }}>
                <span style={{ minWidth: '40px' }}>图标</span>
                <span style={{ minWidth: '50px' }}>ID</span>
                <span style={{ minWidth: '180px' }}>中文名称</span>
                <span style={{ minWidth: '160px' }}>英文名称</span>
                <span style={{ minWidth: '160px' }}>波兰名称</span>
                <span style={{ minWidth: '100px' }}>类型</span>
                <span style={{ flex: 1 }}>操作</span>
              </div>
              
              <Tree
                treeData={filteredTreeData}
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys)}
                showLine
                showIcon={false}
                style={{
                  background: '#fff',
                  padding: '16px',
                  borderRadius: '0 0 6px 6px',
                  border: '1px solid #f0f0f0',
                  borderTop: 'none'
                }}
              />
            </div>
          </Card>
        </div>

        {/* 右侧详情/编辑面板 */}
        <div style={{ flex: '1' }}>
          <Card className="content-card" style={{ height: '100%' }}>
            {rightPanelMode === 'detail' && selectedItem ? (
              <div>
                <h3 style={{ marginBottom: '16px', color: '#1890ff' }}>规格详情</h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={{ fontWeight: 500, color: '#666' }}>ID:</label>
                    <div style={{ marginTop: '4px', fontSize: '14px' }}>{selectedItem.id}</div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#666' }}>中文名称:</label>
                    <div style={{ marginTop: '4px', fontSize: '14px' }}>{selectedItem.name}</div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#666' }}>英文名称:</label>
                    <div style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                      {selectedItem.nameEn || '-'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#666' }}>波兰名称:</label>
                    <div style={{ marginTop: '4px', fontSize: '14px', color: '#999' }}>
                      {selectedItem.namePl || '-'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#666' }}>类型:</label>
                    <div style={{ marginTop: '4px' }}>
                      <Tag color={selectedItem.category === 'option' ? 'blue' : 'green'}>
                        {selectedItem.category === 'option' ? '分类' : '内容'}
                      </Tag>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      父级:
                      <Dropdown
                        menu={{
                          items: Object.entries(LANGUAGE_CONFIG).map(([key, config]) => ({
                            key,
                            label: `${config.flag} ${config.name}`,
                            onClick: () => setParentLanguage(key as LanguageType)
                          }))
                        }}
                        placement="bottomRight"
                      >
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<GlobalOutlined />}
                          style={{ 
                            padding: '2px 4px',
                            fontSize: '10px',
                            height: 'auto',
                            color: parentLanguage === 'zh' ? '#1890ff' : parentLanguage === 'en' ? '#52c41a' : '#faad14'
                          }}
                          title="选择显示语言"
                        >
                          {LANGUAGE_CONFIG[parentLanguage].flag}
                        </Button>
                      </Dropdown>
                    </label>
                    <div style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                      {selectedItem.pId ? `(ID: ${selectedItem.pId}) ${getParentName(selectedItem.pId, parentLanguage)}` : '无'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#666' }}>创建时间:</label>
                    <div style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                      {selectedItem.gmtCreate}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#666' }}>修改时间:</label>
                    <div style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                      {selectedItem.gmtModified}
                    </div>
                  </div>
                  {selectedItem.remark && (
                    <div>
                      <label style={{ fontWeight: 500, color: '#666' }}>备注:</label>
                      <div style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                        {selectedItem.remark}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '24px', display: 'flex', gap: '8px' }}>
                  <Button 
                    type="primary" 
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(selectedItem)}
                  >
                    编辑
                  </Button>
                  <Popconfirm
                    title={t('confirmDeleteTitle')}
                    description={t('confirmDeleteSpec')}
                    onConfirm={() => handleDelete(selectedItem.id)}
                    okText={t('confirm')}
                    cancelText={t('cancel')}
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      删除
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            ) : rightPanelMode === 'add' || rightPanelMode === 'edit' ? (
              <div>
                <h3 style={{ marginBottom: '16px', color: '#1890ff' }}>
                  {rightPanelMode === 'edit' ? '编辑规格' : '新增规格'}
                </h3>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSave}
                >
                  <Form.Item
                    label="类型"
                    name="category"
                    rules={[{ required: true, message: '请选择类型' }]}
                  >
                    <Select placeholder="请选择类型" disabled={!!editingItem}>
                      <Option value="option">分类</Option>
                      <Option value="content">内容</Option>
                    </Select>
                  </Form.Item>

                  {form.getFieldValue('category') === 'content' && (
                    <Form.Item
                      label={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>父级分类</span>
                          <Dropdown
                            menu={{
                              items: Object.entries(LANGUAGE_CONFIG).map(([key, config]) => ({
                                key,
                                label: `${config.flag} ${config.name}`,
                                onClick: () => setParentLanguage(key as LanguageType)
                              }))
                            }}
                            placement="bottomRight"
                          >
                            <Button 
                              type="text" 
                              size="small" 
                              icon={<GlobalOutlined />}
                              style={{ 
                                padding: '2px 4px',
                                fontSize: '12px',
                                height: 'auto',
                                color: parentLanguage === 'zh' ? '#1890ff' : parentLanguage === 'en' ? '#52c41a' : '#faad14'
                              }}
                              title="选择显示语言"
                            >
                              {LANGUAGE_CONFIG[parentLanguage].flag}
                            </Button>
                          </Dropdown>
                        </div>
                      }
                      name="pId"
                      rules={[{ required: true, message: '请选择父级分类' }]}
                    >
                      <div>
                        {parentId && parentId !== 0 ? (
                          <div style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #d9d9d9', 
                            borderRadius: '6px',
                            backgroundColor: '#fafafa',
                            color: '#666'
                          }}>
                            (ID: {parentId}) {getParentName(parentId, parentLanguage)}
                          </div>
                        ) : (
                          <Select 
                            placeholder="请选择父级分类"
                            onChange={(value) => form.setFieldsValue({ pId: value })}
                          >
                            {treeData
                              .filter(item => item.specItem?.category === 'option')
                              .map(item => (
                                <Option key={item.specItem!.id} value={item.specItem!.id}>
                                  (ID: {item.specItem!.id}) {getParentName(item.specItem!.id, parentLanguage)}
                                </Option>
                              ))}
                          </Select>
                        )}
                      </div>
                    </Form.Item>
                  )}
                  
                  <Form.Item
                    label="中文名称"
                    name="name"
                    rules={[{ required: true, message: '请输入中文名称' }]}
                  >
                    <Input placeholder="请输入中文名称" />
                  </Form.Item>
                  
                  <Form.Item
                    label="英文名称"
                    name="nameEn"
                  >
                    <Input placeholder="请输入英文名称" />
                  </Form.Item>
                  
                  <Form.Item
                    label="波兰名称"
                    name="namePl"
                  >
                    <Input placeholder="请输入波兰名称" />
                  </Form.Item>
                  
                  <Form.Item
                    label="备注"
                    name="remark"
                  >
                    <Input.TextArea placeholder="请输入备注" rows={3} />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0 }}>
                    <Space>
                      <Button type="primary" htmlType="submit">
                        {rightPanelMode === 'edit' ? '更新' : '保存'}
                      </Button>
                      <Button onClick={() => setRightPanelMode('detail')}>
                        取消
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: '#999',
                fontSize: '16px'
              }}>
                请选择左侧规格项查看详情
              </div>
            )}
          </Card>
        </div>
      </div>

    </div>
  );
};

export default SpecManagement;